<template>
    <div class="o-grid -push-1">
        <div class="o-grid-10 sm-:12">
            <!-- Spinner -->
            <div :class="['o-spinner_wrap', { 'is-loading-listing': loading }]">
                <Spinner/>
            </div>
            <!-- Filters -->
            <div class="c-events-search u-mb-2xl o-form">
                <div class="c-events-search__row">
                    <!-- Catégories -->
                    <div class="o-form-group c-events-search__row__item">
                        <label class="o-label sr-only" for="categorie">Catégorie</label>
                        <div class="o-select__wrap o-input__icon">
                            <select class="o-select" id="categorie" name="Catégorie" @change="addFilter('categories', $event.target.value)">
                                <option value="" disabled selected>Catégories</option>
                                <option v-for="category in allCategories" :key="category.id" :value="category.slug" v-html="category.name"></option>
                            </select>
                        </div>
                    </div>
                    <!-- Lieux -->
                    <div class="o-form-group c-events-search__row__item">
                        <label class="o-label sr-only" for="salle">Salle</label>
                        <div class="o-select__wrap o-input__icon">
                            <select class="o-select" id="salle" name="Salle" @change="addFilter('salles', $event.target.value)">
                                <option value="" disabled selected>Lieux</option>
                                <option v-for="salle in allSalles" :key="salle.post_name" :value="salle.post_name" v-html="salle.post_title"></option>
                            </select>
                        </div>
                    </div>
                    <div class="c-events-search__row__separator"></div>
                    <div class="o-form-group c-events-search__row__item">
                        <label class="o-label sr-only" for="search">Recherchez par mot.s-clé.s</label>
                        <div class="o-input__icon">
                            <input class="o-input" type="search" v-model="search" id="search" name="Recherchez par mot.s-clé.s" placeholder="Recherchez par mot.s-clé.s" @keyup="updateSearch()">
                            <button class="o-input__icon__button" @click="scrollToResults()">
                                <Image id="search" />
                            </button>
                        </div>
                    </div>
                </div>
                <!-- Tags -->
                <div class="c-events-search__tags">
                    <div  class="c-events-search__tags__list u-hide-under-sm">
                        <!-- Types -->
                        <div v-if="viewMode == 'grid'" class="o-checkbox__wrap u-hide-under-sm" v-for="type in allTypes" :key="type.term_id">
                            <input 
                                class="o-checkbox" 
                                type="checkbox" 
                                :id="`type-${type.term_id}`" 
                                :value="type.slug" 
                                v-model="selectedTypes"
                                @change="filterSpectacles(true)"
                            >
                            <label class="o-checkbox-label" :for="`type-${type.term_id}`">
                                {{ type.name }}
                            </label>
                        </div>
                    </div>
                    <div class="c-events-search__tags__button">
                        <button v-if="viewMode == 'cal'" @click="viewMode = 'grid'" class="o-button -button-no-bg u-hide-under-sm">
                            Programmation <Image id="grid" />
                        </button>
                        <button v-else @click="viewMode = 'cal'" class="o-button -button-no-bg u-hide-under-sm">
                            Calendrier <Image id="calendar" />
                        </button>
                        <button class="o-button -button-no-bg" @click="openAdvancedSearch" data-modal=".js-modal-search" aria-label="Recherche avancée">
                            <span class="o-button__text">Recherche avancée</span>
                            <span class="sr-only">Recherche avancée</span>
                            <Image id="filter" />
                        </button>
                    </div>
                </div>
                <ul v-if="outputFilters().length" class="c-filter__list u-mt-xs">
                    <li v-for="tag in outputFilters()" :key="tag.value" class="c-filter__item">
                        <button class="c-filter__item__close" @click="deleteFilter(tag.type, tag.value)">
                            <Image id="close" />
                        </button>
                        <span class="c-filter__item__title" v-html="tag.name"></span>
                    </li>
                </ul>
                <div class="c-events-search__results">
                    <div class="c-events-search__results__total">{{ totalResults }} résultats</div>
                    <button v-if="outputFilters().length" class="o-button -button-show-results -outline" @click="resetFilters" aria-label="Réinitialiser la recherche">
                        <span class="o-button__text">Réinitialiser la recherche</span>
                        <span class="sr-only">Réinitialiser la recherche</span>
                        <Image id="reload" />
                    </button>
                </div>
            </div>
            <!-- Listing in Calendar mode -->
            <div v-if="viewMode == 'cal'">
                <CalendarMode :spectacles="spectacles" :filterDates="dates" @resetDates="resetDates" @changeDates="updateDates" />
            </div>
            <!-- Listing in Default mode -->
            <div v-else class="c-events-list">
                <div v-for="spectacle in spectacles" :key="spectacle.id" class="c-event-card">
                    <a :href="spectacle.url" class="c-event-card__link">
                        <div class="c-event-card__image-wrap">
                            <div v-if="spectacle.image" v-html="spectacle.image" class="c-event-card__image">
                            </div>
                            <div v-else class="c-event-card__image">
                                <Image id="event-placeholder" class="c-event-card__icon -ico-fill" />
                            </div>
                            <template v-if="spectacle.tag">
                                <div v-for="(tag, slug) in spectacle.tag" :class="'-' + slug" class="c-event-card__tag">{{ tag }}</div>
                            </template>
                        </div>
                        <div class="c-event-card__content">
                            <div class="c-event-card__artist is-style-lowercase" v-html="spectacle.nom_spectacle"></div>
                            <div v-if="spectacle.nom_spectacle != spectacle.artiste" class="c-event-card__title" v-html="'— '+spectacle.artiste"></div>
                            <!-- get spectacle category -->
                            <template v-if="spectacle.categories.length > 0">
                                <span class="o-tags">
                                    <span v-for="category in spectacle.categories" :key="category.id" class="o-tag">
                                        {{ format_category_name(category.name) }}
                                    </span>
                                </span>
                            </template>
                            <template v-else-if="spectacle.types.length > 0">
                                <span class="o-tags">
                                    <span v-for="type in spectacle.types" :key="type.id" class="o-tag">
                                        {{ type.name }}
                                    </span>
                                </span>
                            </template>
                            <div class="c-event-card__text">
                                <template v-if="spectacle.representations.length > 1">
                                    <span class="c-event-card__text__el">
                                        Voir toutes les dates
                                        <Image id="arrow-up-right" class="c-event-card__icon" />
                                    </span>
                                </template>
                                <template v-else>
                                    <span class="c-event-card__text__el">
                                        {{ spectacle.representations[0].dates_format.date_visual }}
                                        <Image id="arrow-up-right" class="c-event-card__icon" />
                                    </span>
                                </template>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
            <!-- Pager -->
            <Pagination
                v-if="pagination.pages.length > 1"
                :pagination="pagination"
            />
            <div v-if="search" class="c-other-results">
                <div class="o-section-title__wrap u-anim-opacity-up is-inview" data-scroll="" data-scroll-offset="10%">
                    <h2 class="o-title-2 o-section-title">Autres résultats</h2>
                    <span class="o-section-title__line u-anim-clip-from-left is-inview" data-scroll="" data-scroll-offset="10%"></span>
                </div>
                <div v-html="otherResults"></div>
            </div>
        </div>
    </div>
</template>

<script>
import Fuse from 'fuse.js'
import Spinner from './parts/Spinner.vue'
import CalendarMode from './CalendarMode.vue'
import Pagination from './parts/Pagination.vue'
import Image from './parts/Image.vue'
import { toRaw } from 'vue';

export default {
    data() {
        return {
            loading: true,
            allSpectacles: [],
            allCategories: [],
            allTypes: [],
            allSalles: [],
            selectedCategories: [],
            selectedTypes: [],
            selectedSalles: [],
            dates: {
                start: '',
                end: ''
            },
            pagination: {
                perPage: 12,
                currentPage: 1,
                pages:[],
            },
            filteredSpectacles: [],
            spectacles: [],
            search: '',
            totalResults: 0,
            viewMode: 'grid',
            otherResults: '',
        }
    },
    components: {
        Spinner,
        Pagination,
        Image,
        CalendarMode
    },
    watch: {
        'pagination.currentPage': function() {
            this.paginate();
        },
        dates: {
            handler() {
                this.filterSpectacles(true, false);
            },
            deep: true
        },
        search: {
            handler() {
                this.filterOtherResults();
            }
        },
        viewMode: {
            handler() {
                this.filterSpectacles(true);
            }
        }
    },
    methods: {
        filterOtherResults(){
            //Fetch html form page like https://sodct.test/?s=moulin and put it in otherResults
            if(this.search.length > 1){
                fetch("/?s="+this.search)
                .then(response => response.text())
                .then(data => {
                    this.otherResults = data;
                })
                .catch(error => {
                    console.error('Error fetching other results:', error);
                });
            }else{
                this.otherResults = '';
            }

        },
        updateDates(dates) {
            this.dates = dates;
            this.filterSpectacles(true, false);
        },
        resetDates() {
            this.dates.start = "";
            this.dates.end = "";
            this.filterSpectacles(true, false);
        },
        updateSearch() {
            this.filterSpectacles(true);
            document.$trigger('updateFilters',{
                from: "listing",
                selectedCategories: this.selectedCategories,
                selectedTypes: this.selectedTypes,
                selectedSalles: this.selectedSalles,
                dates: this.dates,
                search: this.search
            });
        },
        scrollToResults() {
            const results = document.querySelector('.c-events-list');
            window.locomotiveScroll.scrollTo(results, { offset: -120 });
        },
        outputFilters() {
            let tags = [];
            if (this.selectedCategories.length > 0) {
                for (let category of this.selectedCategories) {
                    if (!this.allCategories) return;
                    const foundCategory = this.allCategories.find(c => c.slug === category);
                    if (foundCategory) {
                        tags.push({
                            type: 'categories',
                            value: category,
                            name: foundCategory.name
                        });
                    }
                }
            }
            /*if (this.selectedTypes.length > 0) {
                for (let type of this.selectedTypes) {
                    if(!this.allTypes) return;
                    tags.push({
                        type: 'types',
                        value: type,
                        name: this.allTypes.find(t => t.slug === type).name
                    });
                }
            }*/
            if (this.selectedSalles.length > 0) {
                for (let salle of this.selectedSalles) {
                    if(!this.allSalles) return;
                    let salleName = this.allSalles.find(s => s.post_name === salle);
                    if(salleName){
                        tags.push({
                            type: 'salles',
                            value: salle,
                            name: salleName.post_title
                        });
                    }
                }
            }
            if(this.dates.start && this.dates.end && this.viewMode != 'cal') {
                tags.push({
                    type: 'dates',
                    value: this.dates.start + ' - ' + this.dates.end,
                    name: this.dates.start + ' - ' + this.dates.end
                });
            }
            return tags;
        },
        addFilter(type, value) {
            if (type === 'categories') {
                if (this.selectedCategories.includes(value)) {
                    this.selectedCategories = this.selectedCategories.filter(category => category !== value);
                } else {
                    this.selectedCategories.push(value);
                }
            }
            if (type === 'types') {
                if (this.selectedTypes.includes(value)) {
                    this.selectedTypes = this.selectedTypes.filter(type => type !== value);
                } else {
                    this.selectedTypes.push(value);
                }
            }
            if (type === 'salles') {
                if (this.selectedSalles.includes(value)) {
                    this.selectedSalles = this.selectedSalles.filter(salle => salle !== value);
                } else {
                    this.selectedSalles.push(value);
                }
            }
            this.filterSpectacles(true);
            this.resetInput();
            document.$trigger('updateFilters',{
                from: "listing",
                selectedCategories: this.selectedCategories,
                selectedTypes: this.selectedTypes,
                selectedSalles: this.selectedSalles,
                dates: this.dates,
                search: this.search
            });
        },
        deleteFilter(type, value) {
            if (type === 'categories') {
                this.selectedCategories = this.selectedCategories.filter(category => category !== value);
            }
            if (type === 'types') {
                this.selectedTypes = this.selectedTypes.filter(type => type !== value);
            }
            if (type === 'salles') {
                this.selectedSalles = this.selectedSalles.filter(salle => salle !== value);
            }
            if (type === 'dates') {
                this.dates.start = '';
                this.dates.end = '';
            }
            this.filterSpectacles(true);
            document.$trigger('updateFilters',{
                from: "listing",
                selectedCategories: this.selectedCategories,
                selectedTypes: this.selectedTypes,
                selectedSalles: this.selectedSalles,
                dates: this.dates,
                search: this.search
            });
        },
        resetFilters() {
            this.selectedCategories = [];
            this.selectedTypes = [];
            this.selectedSalles = [];
            this.dates.start = '';
            this.dates.end = '';
            this.search = '';
            this.filterSpectacles(true);
            document.$trigger('updateFilters',{
                from: "listing",
                selectedCategories: this.selectedCategories,
                selectedTypes: this.selectedTypes,
                selectedSalles: this.selectedSalles,
                dates: this.dates,
                search: this.search
            });
        },
        resetInput() {
            const inputs = document.querySelectorAll('select');
            inputs.forEach(input => {
                input.value = '';
            });
        },
        fetchListing() {
            this.getParams();
            // Fetch listing from API
            let random = Math.floor(Math.random() * 1000);
            fetch("/wp-json/stereo/v1/spectacles?"+random, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    //'X-WP-Nonce': WPURLS.nonce
                }
            })
            .then(response => response.json())
            .then(data => {
                this.allSpectacles = data.spectacles;
                this.allCategories = this.notEmpty(data.categories, 'show_cat', data.spectacles);
                this.allTypes = this.notEmpty(data.types, 'show_type', data.spectacles);
                this.allSalles = this.notEmpty(data.salles, 'show_salle', data.spectacles);



                this.filterSpectacles();
                document.$trigger('dataLoaded', {
                    from: 'listing',
                    data: data
                });
                this.loading = false;
            })
        },
        notEmpty(data=[], type='', spectacles=[]) {
            if (type === 'show_cat') {
                //base on this.allSpectacles data don't show categories that are not in the spectacles
                const categories = data.filter(category => {
                    return spectacles.some(spectacle => {
                        if(!Array.isArray(spectacle.categories) || spectacle.categories.length === 0) return;
                        return spectacle.categories.some(cat => cat.slug === category.slug);
                    });
                });
                return categories;
            }else if (type === 'show_type') {
                //base on this.allSpectacles data don't show types that are not in the spectacles
                const types = data.filter(type => {
                    return spectacles.some(spectacle => {
                        if(!Array.isArray(spectacle.types) || spectacle.types.length === 0) return;
                        return spectacle.types.some(t => t.slug === type.slug);
                    });
                });
                return types;
            }else if (type === 'show_salle') {
                //base on this.allSpectacles data don't show salles that are not in the spectacles
                const salles = data.filter(salle => {
                    return spectacles.some(spectacle => {
                        return spectacle.salle.post_name === salle.post_name;
                    });
                });
                return salles;
            }

            return data;
        },
        getParams() {
            const urlParams = new URLSearchParams(window.location.search);
            const categories = urlParams.get('categories');
            const types = urlParams.get('types');
            const salles = urlParams.get('salles');
            const start_date = urlParams.get('start_date');
            const end_date = urlParams.get('end_date');
            const search = urlParams.get('search');
            const page = urlParams.get('pa');
            const view = urlParams.get('view');

            if (categories) {
                this.selectedCategories = categories.split(',');
            }
            if (types) {
                this.selectedTypes = types.split(',');
            }
            if (salles) {
                this.selectedSalles = salles.split(',');
            }
            if (start_date) {
                this.dates.start = start_date;
            }
            if (end_date) {
                this.dates.end = end_date;
            }
            if (search) {
                this.search = search;
            }
            if (page) {
                this.pagination.currentPage = page;
            }
            if (view) {
                this.viewMode = view;
            }
        },
        setParams() {
            const urlParams = new URLSearchParams(window.location.search);
            const categories = this.selectedCategories.join(',');
            const types = this.selectedTypes.join(',');
            const salles = this.selectedSalles.join(',');
            const start_date = this.dates.start;
            const end_date = this.dates.end;
            const search = this.search;
            const page = this.pagination.currentPage;
            const view = this.viewMode;

            urlParams.set('categories', categories);
            urlParams.set('types', types);
            urlParams.set('salles', salles);
            urlParams.set('start_date', start_date);
            urlParams.set('end_date', end_date);
            urlParams.set('search', search);
            urlParams.set('pa', page);
            urlParams.set('view', view);

            window.history.replaceState(false, '', `${window.location.pathname}?${urlParams}`);
            //this.loading = false;
            window.dispatchEvent(new Event('resize'));
        },
        async filterSpectacles(resetPage = false, scroll=true) {
            if (resetPage) {
                this.pagination.currentPage = 1;
            }
            this.filteredSpectacles = await Promise.all(this.allSpectacles.map(async spectacle => {
                let show = true;
                if (this.selectedCategories.length > 0) {
                    if(!Array.isArray(spectacle.categories) || spectacle.categories.length === 0) return;
                    if (show) show = spectacle.categories.some(category => this.selectedCategories.includes(category.slug));
                }
                if (this.selectedTypes.length > 0) {
                    if (!Array.isArray(spectacle.types) || spectacle.types.length === 0) return;
                    if (show) show = spectacle.types.some(type => this.selectedTypes.includes(type.slug));
                }
                if (this.selectedSalles.length > 0) {
                    //spectacle.salle.post_name
                    if(show) show = this.selectedSalles.includes(spectacle.salle.post_name);
                }
                if (this.dates.start || this.dates.end) {
                    if(show) show = await this.checkDates(spectacle);
                }
                return show ? spectacle : null;
            }));

            //if representation date_heure is blank or spectacle.days is set remove it if viewMode is cal
            /*if(this.viewMode == 'cal') {
                this.filteredSpectacles = this.filteredSpectacles.filter(spectacle => {
                    if(!spectacle) return false;
                    if(spectacle.representations.length > 0) {
                        return spectacle.representations.some(representation => {
                            return representation.date_heure && representation.date_heure !== '';
                        });
                    }
                });
            }*/

            this.filteredSpectacles = this.filteredSpectacles.filter(spectacle => spectacle);

            if(this.search.length > 1){
                let search = this.search.toLowerCase();
                //remove accents
                search = search.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                const fuse = new Fuse(this.filteredSpectacles, {
                    shouldSort: false,
                    threshold: 0.3,
                    keys: ['artiste', 'nom_spectacle', 'representations.fields.code_local', 'representations.fields.code_representation', 'search', 'categories.name', 'types.name', 'salle.post_title'],
                });
                const result = fuse.search(search);
                this.filteredSpectacles = result;
            }

            this.totalResults = this.filteredSpectacles.length;
            this.paginate(scroll);
        },
        async checkDates(spectacle) {
            let show = false;
            await Promise.all(spectacle.representations.map(async (representation) => {
                //if representation.date_heure only have Y-m-d format add 00:00:00
                if(!representation.date_heure.includes('T')) {
                    representation.date_heure += 'T00:00:00';
                }

                const representationDate = new Date(
                    new Date(representation.date_heure).toLocaleString("en-US", { timeZone: "America/Toronto" })
                );
                const representationEndDate = new Date(
                    new Date(representation.end_date+ "T23:59:59").toLocaleString("en-US", { timeZone: "America/Toronto" })
                );

                const startDate = new Date(
                    new Date(this.dates.start + "T00:00:00").toLocaleString("en-US", { timeZone: "America/Toronto" })
                );
                const endDate = new Date(
                    new Date((this.dates.end ? this.dates.end : this.dates.start) + "T23:59:59").toLocaleString("en-US", { timeZone: "America/Toronto" })
                );

                if (representationDate >= startDate && representationDate <= endDate) {
                    show = true;
                }

                // Générer les jours de la semaine dans la plage
                const days = [];
                for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                    days.push(d.getDay());
                }

                // Vérifier si representation.days contient un jour valide
                if (representation.days && representation.days.length > 0) {
                    // Ne rien faire si la représentation commence après la plage de dates filtrée
                    if (representationDate > endDate) {
                        show = false;
                        return;
                    }
                    // Ne rien faire si la représentation se termine avant la plage de dates filtrée
                    if (representation.end_date && representationEndDate < startDate) {
                        show = false;
                        return;
                    }
                    const repDays = Array.isArray(representation.days)
                        ? toRaw(representation.days).map(day => Number(day))
                        : [Number(representation.days)];
                    // Générer les jours de la semaine dans la plage
                    const hasValidDay = repDays.some(day => days.includes(day));
                    if (hasValidDay) {
                        show = true;
                    } else {
                        show = false;
                    }
                    // Optionally log for debugging
                }
            }));
            return show;
        },
        paginate(scroll=true) {
            const from = (this.pagination.currentPage - 1) * this.pagination.perPage;
            const to = from + this.pagination.perPage;
            this.spectacles = this.filteredSpectacles.slice(from, to);
            //Pages is an array of all pages base on totalResults and perPage
            this.pagination.pages = Array.from({length: Math.ceil(this.totalResults / this.pagination.perPage)}, (v, k) => k + 1);
            this.setParams();
            if(scroll) window.locomotiveScroll.scrollTo(0);
        },
        openAdvancedSearch() {
            const modal = document.querySelector(".js-modal");

            // Hide other modal contents (if needed)
            const modalContents = document.querySelectorAll('[data-modal="content"]');
            modalContents.forEach(content => {
                content.classList.remove("is-active");
            });

            // Show the current modal content
            const overlayPanel = document.querySelector('[data-modal="content"]');
            overlayPanel.classList.add("is-active");

            // If a video is specified, set its content
            if (this.modalVideoContent) {
                const overlayVideo = document.querySelector(".js-modal-video");
                overlayVideo.innerHTML = this.modalVideoContent;
            }

            modal.showModal();
        },
        format_category_name(name) {
        return name.replace(/&amp;/g, '&');
        }
    },
    mounted() {
        this.fetchListing();

        //add event listener update filters
        document.$on('updateFilters', (data) => {
            if(data.detail.from == 'listing') return;
            this.selectedCategories = data.detail.selectedCategories;
            this.selectedTypes = data.detail.selectedTypes;
            this.selectedSalles = data.detail.selectedSalles;
            this.dates = data.detail.dates;
            this.search = data.detail.search;
            this.filterSpectacles(true);
        });
    }
}
</script>